import React from 'react';
import { Button } from '../../../components/button';
import type { DeleteEGiftCardInfoResponse } from '../types/eGiftCard.types';

interface DeleteEGiftCardConfirmModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    data: DeleteEGiftCardInfoResponse | null;
    loading?: boolean;
}

const DeleteEGiftCardConfirmModal: React.FC<DeleteEGiftCardConfirmModalProps> = ({
    open,
    onClose,
    onConfirm,
    data,
    loading = false
}) => {
    if (!open) return null;

    const isLoading = loading || !data;

    const formatCurrency = (amount: number, currency: string = 'USD'): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const isFullDelete = data ? data.redeemed_quantity === 0 : false;
    const title = isFullDelete ? 'Delete E-Gift Card Confirmation' : 'Delete Remaining E-Gift Cards';
    const confirmButtonText = isFullDelete ? 'Delete E-Gift Card' : 'Delete Remaining E-Gift Cards';
    const warningMessage = isFullDelete
        ? 'This action cannot be undone. The e-gift card will be permanently removed from the system.'
        : 'This action cannot be undone. The e-gift-card record will remain in the list.';

    const quantityDisplay = data ? (isFullDelete
        ? data.total_quantity.toString()
        : `${data.total_quantity} (${data.redeemed_quantity} redeemed, ${data.remaining_quantity} remaining)`) : '';

    const refundAmountLabel = isFullDelete ? 'Remaining Value' : 'Refund Amount';

    // Loading skeleton component
    const Skeleton = ({ className }: { className?: string }) => (
        <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
    );

    return (
        <div 
            className="fixed z-50 flex items-center justify-center animate-in fade-in-0 duration-200" 
            style={{
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                minHeight: '100vh',
                position: 'fixed',
                backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isLoading) {
                    onClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-full max-w-lg p-6 relative animate-in zoom-in-95 fade-in-0 slide-in-from-bottom-2 duration-300">
                <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex items-center gap-1">
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                            {isLoading ? (
                                <Skeleton className="w-5 h-5" />
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.0003 18.3334C14.6027 18.3334 18.3337 14.6025 18.3337 10.0001C18.3337 5.39771 14.6027 1.66675 10.0003 1.66675C5.39795 1.66675 1.66699 5.39771 1.66699 10.0001C1.66699 14.6025 5.39795 18.3334 10.0003 18.3334Z" stroke="#E7000B" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 6.66675V10.0001" stroke="#E7000B" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 13.3333H10.0083" stroke="#E7000B" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </div>
                        {isLoading ? (
                            <Skeleton className="h-6 w-64" />
                        ) : (
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {title}
                            </h2>
                        )}
                    </div>

                    {/* Confirmation Question */}
                    {isLoading ? (
                        <Skeleton className="h-4 w-full" />
                    ) : (
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                            {isFullDelete
                                ? 'Are you sure you want to delete the following e-gift card?'
                                : 'You are about to delete the remaining unused e-gift cards. E-gift cards that have already been redeemed will not be affected.'}
                        </p>
                    )}

                    {/* E-gift-card details */}
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                        {isLoading ? (
                            <>
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Merchant</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{data?.merchant_name}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Value</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                        {data && formatCurrency(data.coupon_value, data.refund_info.currency)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Quantity</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{quantityDisplay}</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Refund Information */}
                    <div className="space-y-3">
                        {isLoading ? (
                            <>
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-4 w-full" />
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="text-sm font-semibold text-[#4A5565] dark:text-gray-300">Refund Information</h3>
                                {isFullDelete && (
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        The remaining value will be refunded to your Total Funds.
                                    </p>
                                )}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-900 dark:text-gray-100">{refundAmountLabel}:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            {data && formatCurrency(data.refund_info.refund_amount, data.refund_info.currency)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-900 dark:text-gray-100">Current Total Funds:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            {data && formatCurrency(data.refund_info.current_total_funds, data.refund_info.currency)}
                                        </span>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">New Total Funds:</span>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                {data && formatCurrency(data.refund_info.new_total_funds, data.refund_info.currency)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Warning Message */}
                    {isLoading ? (
                        <Skeleton className="h-4 w-full" />
                    ) : (
                        <p className="text-sm text-[#364153] dark:text-gray-300">{warningMessage}</p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 mt-2">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={isLoading || loading}
                            className="rounded-lg border-gray-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onConfirm}
                            disabled={isLoading || loading}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-lg"
                        >
                            {loading ? 'Processing...' : (isLoading ? 'Loading...' : confirmButtonText)}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteEGiftCardConfirmModal;

